import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
	HelpCircle, Share, ChevronDown, X, Menu, Edit,
	LogOut, Settings, ChevronUp, CreditCard
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getUserContent } from '../../utils/firebaseService';
import { fetchSubscription } from '../../utils/fetchSubscription';
import SharePopup from '../popup/SharePopup';
import FeedbackPopup from '../popup/Feedback';
import { db } from '../../firebase';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

// Helper function to truncate long titles
const truncateTitle = (title, maxLength = 35) => {
    if (!title) return '';
    return title.length > maxLength ? `${title.substring(0, maxLength)}...` : title;
};

const MenuItem = ({ icon, text, hasSubmenu, onClick }) => (
	<div className="flex items-center px-4 py-2 my-1 rounded-md cursor-pointer hover:bg-gray-100 transition-colors" onClick={onClick}>
		<div className="flex-shrink-0">{icon}</div>
		<span className="ml-3 text-sm">{text}</span>
		{hasSubmenu && <ChevronDown className="ml-auto mx-2" size={16} />}
	</div>
);

const sanitizePhotoURL = (url) => {
	if (url?.startsWith('"') && url?.endsWith('"')) {
		return url.slice(1, -1);
	}
	return url;
};

export default function SideNavMobile() {
	const [isOpen, setIsOpen] = useState(false);
	const { user, logout } = useAuth();
	const [recentContent, setRecentContent] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
	const [showProfileMenu, setShowProfileMenu] = useState(false);
	const [showSharePopup, setShowSharePopup] = useState(false);
	const [showFeedbackPopup, setShowFeedbackPopup] = useState(false); // <--- added state
	const profileMenuRef = useRef(null);
	const [userPlan, setUserPlan] = useState('');

	useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth < 768);
		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		const fetchRecentContent = async () => {
			if (user) {
				setIsLoading(true);
				try {
					const content = await getUserContent();
					setRecentContent(content);
				} catch (error) {
				} finally {
					setIsLoading(false);
				}
			}
		};
		fetchRecentContent();
	}, [user]);

	useEffect(() => {
		// Fetch subscription plan using fetchSubscription
		const getPlan = async () => {
			if (user?.uid) {
				try {
					const { subscription } = await fetchSubscription(user.uid);
					setUserPlan(subscription?.plan || '');
				} catch (err) {
					setUserPlan('');
				}
			} else {
				setUserPlan('');
			}
		};
		getPlan();
	}, [user]);

	// Add useEffect for handling clicks outside the dropdown
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
				setShowProfileMenu(false);
			}
		};
		
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const toggleSidebar = () => setIsOpen((prev) => !prev);
	
	const handleContentClick = (content, e) => {
		if (e) e.preventDefault();
		setIsOpen(false);
		navigate(`/c/${content.id}`);
	};

	const handleProfileClick = () => {
		setShowProfileMenu(!showProfileMenu);
	};

	const handleLogout = async () => {
		try {
			await logout();
			setIsOpen(false);
			navigate('/');
		} catch (error) {
		}
	};

	const handleSettings = () => {
		navigate('/settings');
		setIsOpen(false);
		setShowProfileMenu(false);
	};

	const handlePlan = () => {
		navigate('/plans');
		setIsOpen(false);
		setShowProfileMenu(false);
	};

	if (!isMobile) return null;

	return (
		<>
			{/* Mobile Header */}
			<div className="fixed top-0 left-0 right-0 h-19 bg-white border-b border-gray-200 flex items-center justify-between px-4 z-50 shadow">
				<button onClick={toggleSidebar} className="p-2" aria-label="Toggle menu">
					{isOpen ? <X size={24} /> : <Menu size={24} />}
				</button>
				<Link to="/">
					<img src="/1xSkills-Logo.webp" alt="1xSkills" className="h-12" />
				</Link>
				<Link className="p-2" aria-label="Edit" to="/">
					<Edit size={22} />
				</Link>
			</div>

			{/* Sidebar and Overlay */}
			{isOpen && (
				<div className="fixed inset-0 z-50">
					{/* Overlay */}
					<div className="fixed inset-0 bg-white bg-opacity-70" onClick={() => setIsOpen(false)} />

					{/* Sidebar - Increased width from w-72 to w-80 */}
					<div className="fixed top-0 left-0 w-64 h-full bg-white z-50 flex flex-col shadow-md">
						
						{/* Sticky Header */}
						<div className="flex justify-between items-center p-4 border-b">
							<Link to="/" onClick={() => setIsOpen(false)}>
								<img src="/1xSkills-Logo.webp" alt="1xSkills" className="h-10" />
							</Link>
							<button onClick={() => setIsOpen(false)} className="p-2" aria-label="Close sidebar">
								<X size={24} />
							</button>
						</div>

						{/* Scrollable Middle */}
						<div className="flex-1 overflow-y-auto px-4 py-3 w-50">
							{!user ? (
								<div className="mt-6 px-3">
									<h3 className="text-xl font-semibold mb-3">Welcome to 1xSkills</h3>
                <p className="text-sm text-gray-600 mb-2">Your personalized AI Knowledge Workspace</p>
                
                <p className="text-sm text-gray-600 mb-6">
                  Sign in to get started. <br></br><br></br>
                  We're constantly improving the platform, and if you have any feedback, 
                  we'd love to hear from you.
                </p>
								</div>
							) : (
								<>
									{/* Recents */}
									<div className="mt-6">
										<h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">RECENTS</h3>
										{isLoading ? (
											<p className="text-sm text-gray-500">Loading...</p>
										) : recentContent.length > 0 ? (
											recentContent.map((content) => (
												<div key={content.id} className="flex items-center px-3 py-2 rounded-md cursor-pointer hover:bg-gray-100" onClick={(e) => handleContentClick(content, e)}>
													<span className="text-sm truncate">
														{truncateTitle(content.title || content.metadata?.title || `Content ${content.id.substring(0, 8)}`)}
													</span>
												</div>
											))
										) : (
											<p className="text-sm text-gray-500">No recent content</p>
										)}
									</div>
								</>
							)}
						</div>

						{/* Sticky Bottom Section */}
						<div className="border-t p-4">
							{user ? (
								<>
									{/* Help & Tools */}
									<div>
										<h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">HELP & TOOLS</h3>
										<MenuItem icon={<HelpCircle size={18} className="text-gray-600 mx-2" />} text="Feedback" onClick={() => { setIsOpen(false); setShowFeedbackPopup(true); }} />
										<MenuItem icon={<Share size={18} className="text-gray-600 mx-2" />} text="Refer" onClick={() => { setIsOpen(false); setShowSharePopup(true); }} />
									</div>

									{/* User Profile with Dropdown */}
									<div className="mt-4 relative" ref={profileMenuRef}>
										{/* Profile Dropdown Menu - Positioned ABOVE the profile section */}
										{showProfileMenu && (
											<div className="absolute -top-32 left-0 right-0 bg-white rounded-md shadow-lg border border-gray-200 overflow-hidden z-50">
												<div 
													className="flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer"
													onClick={handleSettings}
												>
													<Settings size={16} className="text-gray-600 mr-2" />
													<span className="text-sm">Settings</span>
												</div>
												<div 
													className="flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer"
													onClick={handlePlan}
												>
													<CreditCard size={16} className="text-gray-600 mr-2" />
													<span className="text-sm">Plan</span>
												</div>
												<div 
													className="flex items-center px-4 py-3 hover:bg-gray-100 cursor-pointer border-t border-gray-100"
													onClick={handleLogout}
												>
													<LogOut size={16} className="text-gray-600 mr-2" />
													<span className="text-sm">Log out</span>
												</div>
											</div>
										)}
										
										{/* User Profile - Clickable to toggle dropdown */}
										<div 
											className="flex items-center p-2 rounded-md hover:bg-gray-100 cursor-pointer"
											onClick={handleProfileClick}
										>
											<div className="rounded-full mx-2 bg-blue-100 flex items-center justify-center overflow-hidden">
												{user?.profilePhoto ? (
													<img src={sanitizePhotoURL(user?.profilePhoto)} alt="Profile" className="aspect-[1/1] object-cover" />
												) : (
													<span className="text-lg font-semibold text-blue-600">
														{user?.displayName?.[0] || user?.email?.[0] || 'U'}
													</span>
												)}
											</div>
											<div className="ml-3">
												<p className="text-sm font-medium text-gray-800 truncate">{truncateTitle(user?.displayName || 'User', 20)}</p>
												<p className="text-xs text-gray-500 truncate">{truncateTitle(user?.email || 'No email', 25)}</p>
												{/* Show plan below email */}
												{userPlan && (
													<p className="text-xs text-blue-800 font-bold pt-1">{userPlan} Plan</p>
												)}
											</div>
											{showProfileMenu ? 
												<ChevronUp size={16} className="text-gray-500 ml-auto" /> :
												<ChevronDown size={16} className="text-gray-500 ml-auto" />
											}
										</div>
									</div>
								</>
							) : (
								<Link 
									to="/login" 
									className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md"
									onClick={() => setIsOpen(false)}
								>
									Sign in
								</Link>
							)}
						</div>
					</div>
				</div>
			)}
			{/* Popups */}
			<SharePopup
				isOpen={showSharePopup}
				onClose={() => setShowSharePopup(false)}
				link={typeof window !== 'undefined' ? window.location.origin : ''}
			/>
			<FeedbackPopup
				isOpen={showFeedbackPopup}
				onClose={() => setShowFeedbackPopup(false)}
				initialEmail={user?.email || ''}
				onSubmit={async ({ message }) => {
					if (user?.uid) {
						try {
							await addDoc(collection(db, 'users', user.uid, 'Feedback'), {
								message,
								timestamp: serverTimestamp()
							});
						} catch (error) {
							console.error('Error saving feedback:', error);
							throw error; // re-throw to let FeedbackPopup handle
						}
					}
				}}
			/>
		</>
	);
}